// Character similarity between an AI draft and a clinician's final text.
//
// WHY THIS EXISTS AT ALL, AND WHY IT IS A REIMPLEMENTATION
//
// The number has to be computed where both versions of the text are in hand,
// because the text itself must never enter the warehouse. In the browser, at the
// moment a paragraph is saved, both are in hand - so the comparison happens here
// and only the number travels.
//
// The historical figures for the same measure were produced in Python by
// `difflib.SequenceMatcher(None, a, b).ratio()`. If this implementation differed
// even slightly, the metric would change meaning on the day the switch happened
// and every trend line would show a step that never occurred. So this is a
// deliberate, faithful port of CPython's algorithm rather than a reasonable
// similarity function, and its agreement with Python is verified against a
// generated fixture (see __tests__/textSimilarity.test.js). Do not "improve" the
// algorithm here: any change makes new rows incomparable with old ones.
//
// THE PART MOST REIMPLEMENTATIONS GET WRONG
//
// SequenceMatcher's default `autojunk=True` is active for `SequenceMatcher(None,
// ...)`. Once the second sequence reaches 200 elements, any character occurring
// in more than roughly 1% of it is treated as "popular" and removed from the
// index of where characters can be found. Report paragraphs are always longer
// than 200 characters, so this is the normal case, not an edge case: spaces and
// common letters stop being able to START a match. They can still EXTEND one,
// because the extension loops test membership of the junk set only, and popular
// characters are held separately from junk. Getting either half of that wrong
// produces plausible numbers that quietly disagree with the historical ones.

// CPython: autojunk applies from this length of the second sequence upwards.
const AUTOJUNK_MIN_LENGTH = 200;

/**
 * Index of each character of b to the positions it occupies, with popular
 * characters removed exactly as CPython's __chain_b does.
 */
function buildIndex(b) {
  const b2j = new Map();
  for (let i = 0; i < b.length; i += 1) {
    const ch = b[i];
    const positions = b2j.get(ch);
    if (positions) positions.push(i);
    else b2j.set(ch, [i]);
  }

  // isjunk is null here, so the junk set is empty. It still has to exist,
  // because the extension loops below distinguish junk from popular.
  const bjunk = new Set();

  if (b.length >= AUTOJUNK_MIN_LENGTH) {
    // CPython: ntest = n // 100 + 1, dropped when len(idxs) > ntest.
    const ntest = Math.floor(b.length / 100) + 1;
    const popular = [];
    b2j.forEach((positions, ch) => {
      if (positions.length > ntest) popular.push(ch);
    });
    popular.forEach((ch) => b2j.delete(ch));
  }

  return { b2j, bjunk };
}

/**
 * CPython SequenceMatcher.find_longest_match, restricted to [alo, ahi) x [blo, bhi).
 */
function findLongestMatch(a, b, alo, ahi, blo, bhi, index) {
  const { b2j, bjunk } = index;
  let besti = alo;
  let bestj = blo;
  let bestsize = 0;

  // j2len[j] is the length of the longest match ending at a[i] and b[j]. Rebuilt
  // per i, which is what keeps the algorithm quadratic-in-practice rather than
  // storing the whole matrix.
  let j2len = new Map();
  for (let i = alo; i < ahi; i += 1) {
    const newj2len = new Map();
    const positions = b2j.get(a[i]);
    if (positions) {
      for (let p = 0; p < positions.length; p += 1) {
        const j = positions[p];
        if (j < blo) continue;
        if (j >= bhi) break;
        const k = (j2len.get(j - 1) || 0) + 1;
        newj2len.set(j, k);
        if (k > bestsize) {
          besti = i - k + 1;
          bestj = j - k + 1;
          bestsize = k;
        }
      }
    }
    j2len = newj2len;
  }

  // Extend by non-junk characters on both ends. Popular characters are NOT junk,
  // so a match does extend through them even though it could not have started
  // on one.
  while (besti > alo && bestj > blo
    && !bjunk.has(b[bestj - 1]) && a[besti - 1] === b[bestj - 1]) {
    besti -= 1;
    bestj -= 1;
    bestsize += 1;
  }
  while (besti + bestsize < ahi && bestj + bestsize < bhi
    && !bjunk.has(b[bestj + bestsize])
    && a[besti + bestsize] === b[bestj + bestsize]) {
    bestsize += 1;
  }

  // Then absorb adjoining junk. With isjunk null the junk set is empty, so these
  // are no-ops today; they are kept because their absence would be a silent
  // divergence from the reference algorithm the moment junk is ever configured.
  while (besti > alo && bestj > blo
    && bjunk.has(b[bestj - 1]) && a[besti - 1] === b[bestj - 1]) {
    besti -= 1;
    bestj -= 1;
    bestsize += 1;
  }
  while (besti + bestsize < ahi && bestj + bestsize < bhi
    && bjunk.has(b[bestj + bestsize])
    && a[besti + bestsize] === b[bestj + bestsize]) {
    bestsize += 1;
  }

  return [besti, bestj, bestsize];
}

/**
 * Total number of matched characters: CPython's get_matching_blocks, summed.
 * Iterative rather than recursive - a long paragraph pair can nest deeply, and a
 * blown stack inside a save handler is not an acceptable failure mode.
 */
function totalMatchingSize(a, b, index) {
  let matches = 0;
  const queue = [[0, a.length, 0, b.length]];
  while (queue.length) {
    const [alo, ahi, blo, bhi] = queue.pop();
    const [i, j, k] = findLongestMatch(a, b, alo, ahi, blo, bhi, index);
    if (k === 0) continue;
    matches += k;
    if (alo < i && blo < j) queue.push([alo, i, blo, j]);
    if (i + k < ahi && j + k < bhi) queue.push([i + k, ahi, j + k, bhi]);
  }
  return matches;
}

/**
 * How much of the AI draft survived review, as a percentage from 0 to 100.
 *
 * Returned at full precision deliberately, NOT rounded. Rounding here would have
 * to match Python's round-half-to-even on a binary float to stay comparable with
 * the historical figures, which is a needless thing to depend on; rounding for
 * display belongs in the query. Full precision also makes "accepted unchanged"
 * an exact test: 2*M/T is exactly 1 only when the two texts are identical, since
 * M can equal (len(a)+len(b))/2 only when both lengths equal M.
 *
 * @param {string} aiText - The AI's draft.
 * @param {string} finalText - What the clinician settled on.
 * @returns {number} 0-100. Two empty texts count as identical, matching difflib.
 */
export function charSimilarityPct(aiText, finalText) {
  const aStr = typeof aiText === 'string' ? aiText : String(aiText == null ? '' : aiText);
  const bStr = typeof finalText === 'string' ? finalText : String(finalText == null ? '' : finalText);
  // CODE POINTS, not UTF-16 units. Python iterates characters as code points,
  // so indexing the raw JS string diverges on any astral character: two emoji
  // sharing a high surrogate read as half-matching, and the 200-unit autojunk
  // threshold trips one "character" early. Array.from splits on code points,
  // and every helper below works on the arrays.
  const a = Array.from(aStr);
  const b = Array.from(bStr);

  const total = a.length + b.length;
  // difflib's _calculate_ratio returns 1.0 for two empty sequences.
  if (total === 0) return 100;

  const matches = totalMatchingSize(a, b, buildIndex(b));
  return (2.0 * matches) / total * 100;
}

/**
 * Whitespace-delimited token count, matching the reporting definition
 * (Python's str.split() with no argument, which collapses runs of whitespace and
 * ignores leading and trailing whitespace).
 *
 * @param {string} text
 * @returns {number}
 */
export function wordCount(text) {
  const value = typeof text === 'string' ? text : String(text == null ? '' : text);
  const trimmed = value.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

/**
 * True when the clinician accepted the draft with no change at all.
 *
 * Derived from the similarity only, never from the presence of edit history: an
 * edit that was later reset leaves history behind while the text is the AI's
 * again, and that counts as accepted. `wasEdited` is the exact inverse of this
 * same number rather than a separate calculation, so the two can never disagree
 * about one paragraph.
 *
 * @param {number} similarityPct
 * @returns {boolean}
 */
export function acceptedAsIs(similarityPct) {
  return similarityPct === 100;
}
